// src/components/Products/FeedbackForm/pages/Questions.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  listQuestions,
  deleteQuestion,
  reorderQuestions,
  getGoogleLink,
  patchGoogleLink,
} from '../services/api';
import { useNavigate } from 'react-router-dom';
import { getRestaurante } from '../../../../services/api';
import {
  Edit,
  Trash2,
  GripVertical,
  Plus,
  Link as LinkIcon,
  ExternalLink,
  MoveVertical,
  FileQuestion,
  PenLine,
  Sparkles,
  AlertTriangle,
  X,
  Star,
  Eye,
  EyeOff,
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { getApiErrorMessage } from '../../../../utils/apiError';

const tipoLabels = {
  nps: 'Escala (0–10)',
  escala: 'Escala (1–5)',
  texto: 'Texto',
  sim_nao: 'Sim/Não',
  multipla_escolha: 'Múltipla escolha',
  multipla_escolha_multi: 'Múltipla seleção',
};

const Questions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkinSlug, setCheckinSlug] = useState('');
  const [reorderMode, setReorderMode] = useState(false);
  const [backup, setBackup] = useState([]);
  const [googleLink, setGoogleLinkState] = useState('');
  // Modais
  const [deleteTarget, setDeleteTarget] = useState(null); // item a deletar
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const linkInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await getRestaurante();
        setCheckinSlug(r?.checkin_slug || '');

        const data = await listQuestions(r.id);
        data.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
        setItems(data);

        try {
          const glResp = await getGoogleLink();
          const url = glResp?.google_review_url || '';
          setGoogleLinkState(url);
          try {
            if (url) localStorage.setItem('googleLink', url);
            else localStorage.removeItem('googleLink');
          } catch {
            // localStorage pode estar indisponível (modo privado / quota)
          }
        } catch (err) {
          console.error('Falha ao obter google link:', err);
          // Link Google é opcional — não bloquear a tela de perguntas
        }
      } catch (e) {
        console.error('Erro ao carregar perguntas:', e);
        toast.error(getApiErrorMessage(e, 'Não foi possível carregar as perguntas.'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onDelete = (item) => {
    setDeleteTarget(item);
  };

  const confirmDeleteQuestion = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteQuestion(deleteTarget.id);
      setItems((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      toast.success('Pergunta removida.');
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      toast.error('Não foi possível remover a pergunta.');
    } finally {
      setDeleting(false);
    }
  };

  const startReorder = () => {
    setBackup(items);
    setReorderMode(true);
  };

  const cancelReorder = () => {
    setItems(backup);
    setReorderMode(false);
  };

  const saveOrder = async () => {
    try {
      const payload = items.map((p, i) => ({ id: p.id, ordem: i + 1 }));
      await reorderQuestions(payload);
      setItems((prev) => prev.map((p, i) => ({ ...p, ordem: i + 1 })));
      toast.success('Ordem salva com sucesso!');
    } catch (e) {
      console.error(e);
      toast.error('Falha ao salvar a ordem.');
    }
    setReorderMode(false);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const next = Array.from(items);
    const [removed] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, removed);
    setItems(next);
  };

  const handleCopyLink = async () => {
    try {
      const slug = checkinSlug;
      if (!slug) {
        toast.error('Slug do restaurante não encontrado.');
        return;
      }

      const url = new URL(`${window.location.origin}/pesquisa`);
      url.searchParams.set('restaurante', slug);

      if (googleLink) {
        url.searchParams.set('gl', googleLink);
      }

      await navigator.clipboard.writeText(url.toString());
      toast.success('Link copiado para a área de transferência!');
    } catch (err) {
      console.error('Erro ao copiar link:', err);
      toast.error('Não foi possível copiar o link.');
    }
  };

  const handleOpenGoogle = () => {
    if (googleLink) {
      window.open(googleLink, '_blank', 'noopener,noreferrer');
      return;
    }
    handleSetGoogleLink();
  };

  const handleSetGoogleLink = () => {
    setLinkInput(googleLink || '');
    setLinkModalOpen(true);
  };

  const saveGoogleLink = async () => {
    const trimmed = (linkInput || '').trim();
    setLinkSaving(true);
    try {
      const resp = await patchGoogleLink({ google_review_url: trimmed });
      const saved = resp?.google_review_url || '';
      setGoogleLinkState(saved);

      try {
        if (saved) localStorage.setItem('googleLink', saved);
        else localStorage.removeItem('googleLink');
      } catch {
        // localStorage pode estar indisponível (modo privado / quota)
      }

      if (saved) toast.success('Link do Google salvo!');
      else toast.info('Link do Google removido.');
      setLinkModalOpen(false);
    } catch (err) {
      console.error('Erro ao salvar link do Google:', err);
      toast.error('Não foi possível salvar o link. Tente novamente.');
    } finally {
      setLinkSaving(false);
    }
  };

  // ESC fecha modais e foco automático no input do link
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (linkModalOpen) setLinkModalOpen(false);
      if (deleteTarget) setDeleteTarget(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [linkModalOpen, deleteTarget]);

  useEffect(() => {
    if (linkModalOpen && linkInputRef.current) {
      const t = setTimeout(() => {
        linkInputRef.current?.focus();
        linkInputRef.current?.select();
      }, 50);
      return () => clearTimeout(t);
    }
  }, [linkModalOpen]);

  // -- Sub-components (mantidos inline para escopo desta página) --

  const SecondaryButton = ({ icon: Icon, children, onClick, title }) => (
    <button
      onClick={onClick}
      title={title}
      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 hover:border-slate-300 hover:text-slate-900 transition shadow-sm"
    >
      {Icon && <Icon size={15} />}
      {children}
    </button>
  );

  const PrimaryButton = ({ icon: Icon, children, onClick }) => (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2E8B57] text-white text-sm font-semibold hover:bg-[#247044] transition shadow-[0_4px_12px_rgba(46,139,87,0.18)] hover:shadow-[0_6px_16px_rgba(46,139,87,0.28)]"
    >
      {Icon && <Icon size={15} />}
      {children}
    </button>
  );

  const TipoChip = ({ children }) => (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide border border-slate-200 text-slate-600 bg-slate-50">
      {children}
    </span>
  );

  const ObrigatoriaChip = ({ obrigatoria }) => (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide border ${
        obrigatoria
          ? 'border-[#D7A64A]/40 text-[#7B4B0C] bg-[#FFF4E4]'
          : 'border-slate-200 text-slate-500 bg-slate-50'
      }`}
    >
      {obrigatoria ? 'Obrigatória' : 'Opcional'}
    </span>
  );

  const AtivoChip = ({ ativo }) => (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide border ${
        ativo
          ? 'border-[#2E8B57]/40 text-[#1F5C3A] bg-[#EAF7EE]'
          : 'border-slate-300 text-slate-500 bg-slate-100'
      }`}
    >
      {ativo ? <Eye size={12} /> : <EyeOff size={12} />}
      {ativo ? 'Ativa' : 'Inativa'}
    </span>
  );

  return (
    <div className="px-2 sm:px-6 pb-12 sm:pb-16">
      <div
        className="max-w-[1280px] mx-auto rounded-2xl sm:rounded-3xl border p-3 sm:p-5 md:p-6 overflow-hidden space-y-5"
        style={{
          borderColor: 'rgba(15, 23, 42, 0.06)',
          background:
            'radial-gradient(900px circle at 10% -20%, rgba(251, 236, 214, 0.55) 0%, transparent 55%), radial-gradient(1000px circle at 90% 0%, rgba(214, 234, 248, 0.55) 0%, transparent 60%), #F6F4F1',
        }}
      >
      {/* Hero header */}
      <div className="flex flex-col items-center text-center gap-1.5 px-2 pt-2">
        <div className="flex items-center gap-2">
          <FileQuestion size={22} className="text-[#1F5C3A]" />
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
            Perguntas do formulário
          </h2>
        </div>
        <p className="text-sm text-slate-500 max-w-xl leading-relaxed">
          Organize as perguntas que seus clientes verão. Edite, reordene ou crie novas.
        </p>
      </div>

      {/* Toolbar */}
      <div className="rounded-2xl border border-slate-200/70 bg-white/95 backdrop-blur-sm shadow-[0_4px_16px_rgba(15,23,42,0.04)] p-3 sm:p-4">
        {!reorderMode ? (
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
            <div className="flex flex-wrap gap-2">
              <SecondaryButton
                icon={LinkIcon}
                onClick={handleCopyLink}
                title="Copia o link público do formulário para enviar aos clientes"
              >
                Copiar link
              </SecondaryButton>
              <SecondaryButton
                icon={ExternalLink}
                onClick={handleOpenGoogle}
                title={googleLink ? 'Abrir avaliações no Google' : 'Definir link do Google'}
              >
                Abrir no Google
              </SecondaryButton>
            </div>

            <div className="flex flex-wrap gap-2 lg:ml-auto">
              <PrimaryButton
                icon={Plus}
                onClick={() => navigate('/feedback-hero/form/perguntas/novo')}
              >
                Nova pergunta
              </PrimaryButton>
              {items.length > 1 && (
                <SecondaryButton icon={MoveVertical} onClick={startReorder}>
                  Reordenar
                </SecondaryButton>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="inline-flex items-center gap-2 text-sm text-[#7B4B0C] font-medium px-3 py-1.5 rounded-lg bg-[#FFF4E4] border border-[#D7A64A]/30">
              <Sparkles size={14} />
              Arraste as perguntas para alterar a ordem.
            </div>
            <div className="flex gap-2">
              <SecondaryButton onClick={cancelReorder}>Cancelar</SecondaryButton>
              <PrimaryButton onClick={saveOrder}>Salvar ordem</PrimaryButton>
            </div>
          </div>
        )}

        {/* Google link bar (dentro da toolbar para não criar nova linha) */}
        {!reorderMode && (
          <div className="mt-3 pt-3 border-t border-slate-200/70 text-xs text-slate-500 flex items-start gap-1.5 flex-wrap">
            {googleLink ? (
              <>
                <span className="font-medium text-slate-600">Link do Google:</span>
                <a
                  className="text-[#1F5C3A] underline-offset-2 hover:underline truncate max-w-[420px]"
                  href={googleLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  {googleLink.length > 70 ? `${googleLink.slice(0, 70)}…` : googleLink}
                </a>
                <button
                  onClick={handleSetGoogleLink}
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#2E8B57] hover:text-[#1F5C3A] font-semibold ml-1"
                >
                  <PenLine size={11} />
                  Editar
                </button>
              </>
            ) : (
              <>
                <span>Nenhum link do Google definido.</span>
                <button
                  onClick={handleSetGoogleLink}
                  className="text-[11px] uppercase tracking-wider text-[#2E8B57] hover:text-[#1F5C3A] font-semibold"
                >
                  Definir agora
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Loading / Empty state */}
      {loading && (
        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 text-slate-500 text-sm">
          Carregando…
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center">
          <FileQuestion size={32} className="text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-700">Nenhuma pergunta ainda</p>
          <p className="text-xs text-slate-500 mt-1">
            Comece criando sua primeira pergunta para o formulário.
          </p>
          <button
            onClick={() => navigate('/feedback-hero/form/perguntas/novo')}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2E8B57] text-white text-sm font-semibold hover:bg-[#247044] transition shadow-[0_4px_12px_rgba(46,139,87,0.18)]"
          >
            <Plus size={15} />
            Criar primeira pergunta
          </button>
        </div>
      )}

      {/* Questions list */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="questions">
          {(provided) => (
            <ul className="space-y-2.5" ref={provided.innerRef} {...provided.droppableProps}>
              {items.map((q, i) => {
                const displayOrder = reorderMode ? i + 1 : q.ordem || i + 1;
                const isAtiva = q.ativo !== false;
                return (
                  <Draggable
                    key={q.id}
                    draggableId={String(q.id)}
                    index={i}
                    isDragDisabled={!reorderMode}
                  >
                    {(dragProvided, snapshot) => (
                      <li
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={`group relative p-4 bg-white border rounded-2xl flex items-start justify-between gap-4 transition-all duration-200 ${
                          snapshot.isDragging
                            ? 'border-[#2E8B57]/40 shadow-[0_12px_32px_rgba(46,139,87,0.18)]'
                            : 'border-slate-200/70 shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:border-[#2E8B57]/30 hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)]'
                        }`}
                      >
                        {/* Left accent stripe */}
                        <span
                          aria-hidden="true"
                          className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${
                            isAtiva ? 'bg-[#2E8B57]/40' : 'bg-slate-300'
                          }`}
                        />

                        <div className="flex items-start gap-3 pl-2 min-w-0 flex-1">
                          {reorderMode && (
                            <span
                              className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#1F5C3A] mt-1 transition"
                              {...dragProvided.dragHandleProps}
                              title="Arrastar"
                            >
                              <GripVertical size={18} />
                            </span>
                          )}
                          <span className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-lg text-xs font-bold border border-[#2E8B57]/30 text-[#1F5C3A] bg-[#EAF7EE] shadow-sm">
                            #{displayOrder}
                          </span>
                          <div className="space-y-2 min-w-0 flex-1">
                            <div
                              className={`text-[15px] font-semibold leading-snug break-words ${
                                isAtiva ? 'text-slate-800' : 'text-slate-400'
                              }`}
                            >
                              {q.texto || (
                                <span className="text-slate-400 italic font-medium">
                                  Pergunta sem texto
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <AtivoChip ativo={isAtiva} />
                              <TipoChip>Tipo: {tipoLabels[q.tipo] || q.tipo}</TipoChip>
                              <ObrigatoriaChip obrigatoria={q.obrigatoria} />
                            </div>
                          </div>
                        </div>

                        {!reorderMode && (
                          <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition flex-shrink-0">
                            <button
                              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-[#EAF7EE] hover:border-[#2E8B57]/30 hover:text-[#1F5C3A] transition"
                              onClick={() => navigate(`/feedback-hero/form/perguntas/${q.id}`)}
                              title="Editar"
                              aria-label="Editar pergunta"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-[#FCEAE7] hover:border-[#D46D63]/40 hover:text-[#7A2F2A] transition"
                              onClick={() => onDelete(q)}
                              title="Remover"
                              aria-label="Remover pergunta"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </li>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
      </div>

      {/* Modal: editar/definir link do Google */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {linkModalOpen && (
            <motion.div
              key="link-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
              onClick={() => !linkSaving && setLinkModalOpen(false)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="link-modal-title"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 8 }}
                transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-md bg-white rounded-2xl border border-slate-200/70 shadow-[0_24px_64px_rgba(15,23,42,0.18)] overflow-hidden"
              >
                {/* close button */}
                <button
                  onClick={() => !linkSaving && setLinkModalOpen(false)}
                  className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
                  aria-label="Fechar"
                >
                  <X size={16} />
                </button>

                <div className="p-6">
                  {/* icon header */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#EAF7EE] border border-[#2E8B57]/20 flex items-center justify-center text-[#1F5C3A]">
                      <Star size={18} />
                    </div>
                    <div>
                      <h3
                        id="link-modal-title"
                        className="text-base font-bold text-slate-800 leading-tight"
                      >
                        Link das avaliações do Google
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Após o feedback, os clientes serão convidados a avaliar no Google.
                      </p>
                    </div>
                  </div>

                  <label
                    htmlFor="google-link-input"
                    className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-4 mb-1.5"
                  >
                    URL completa
                  </label>
                  <div className="relative">
                    <LinkIcon
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                    <input
                      id="google-link-input"
                      ref={linkInputRef}
                      type="url"
                      value={linkInput}
                      onChange={(e) => setLinkInput(e.target.value)}
                      placeholder="https://g.page/r/..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !linkSaving) {
                          e.preventDefault();
                          saveGoogleLink();
                        }
                      }}
                      className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2E8B57]/30 focus:border-[#2E8B57] transition"
                    />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    Dica: você encontra esse link em <span className="font-medium text-slate-500">Google Maps → seu restaurante → Compartilhar → Avaliações</span>.
                  </p>

                  <div className="flex items-center justify-end gap-2 mt-5">
                    {googleLink && (
                      <button
                        onClick={() => {
                          setLinkInput('');
                        }}
                        disabled={linkSaving}
                        className="mr-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-[#7A2F2A] hover:bg-[#FCEAE7] transition disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                        Remover link
                      </button>
                    )}
                    <button
                      onClick={() => setLinkModalOpen(false)}
                      disabled={linkSaving}
                      className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={saveGoogleLink}
                      disabled={linkSaving}
                      className="px-4 py-2 rounded-lg bg-[#2E8B57] text-white text-sm font-semibold hover:bg-[#247044] transition shadow-[0_4px_12px_rgba(46,139,87,0.18)] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {linkSaving ? 'Salvando…' : 'Salvar link'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Modal: confirmar exclusão de pergunta */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {deleteTarget && (
            <motion.div
              key="delete-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
              onClick={() => !deleting && setDeleteTarget(null)}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="delete-modal-title"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 8 }}
                transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-md bg-white rounded-2xl border border-slate-200/70 shadow-[0_24px_64px_rgba(15,23,42,0.18)] overflow-hidden"
              >
                <button
                  onClick={() => !deleting && setDeleteTarget(null)}
                  className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
                  aria-label="Fechar"
                >
                  <X size={16} />
                </button>

                <div className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#FCEAE7] border border-[#D46D63]/30 flex items-center justify-center text-[#7A2F2A] flex-shrink-0">
                      <AlertTriangle size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3
                        id="delete-modal-title"
                        className="text-base font-bold text-slate-800 leading-tight"
                      >
                        Remover esta pergunta?
                      </h3>
                      <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                        Esta ação não pode ser desfeita. Respostas já recebidas para esta pergunta serão mantidas.
                      </p>

                      {deleteTarget?.texto && (
                        <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200/70">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                            Pergunta
                          </div>
                          <div className="text-sm font-medium text-slate-700 break-words">
                            {deleteTarget.texto}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 mt-5">
                    <button
                      onClick={() => setDeleteTarget(null)}
                      disabled={deleting}
                      className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={confirmDeleteQuestion}
                      disabled={deleting}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#A5342B] text-white text-sm font-semibold hover:bg-[#8A2A22] transition shadow-[0_4px_12px_rgba(165,52,43,0.22)] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={14} />
                      {deleting ? 'Removendo…' : 'Sim, remover'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default Questions;
